import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Grid,
  LinearProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  LoadOrganizationMembersDocument,
  LoadSpaceMembersDocument,
  useLoadOrganizationMembersQuery,
  useLoadSpaceMembersQuery,
  useRemoveOrganizationMembershipMutation,
  useRemoveSpaceMembershipMutation,
  useSetOrganizationMembershipMutation,
  useSetSpaceMembershipMutation,
} from "~/operations";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useViewer } from "~/providers/viewer";
import { useCanSetSpaceMembership } from "~/lib/iam/actions/mondoo/captain/Captain/SetSpaceMembership";
import { useCanSetOrganizationMembership } from "~/lib/iam/actions/mondoo/captain/Captain/SetOrganizationMembership";
import { useCanRemoveSpaceMembership } from "~/lib/iam/actions/mondoo/captain/Captain/RemoveSpaceMembership";
import { useCanRemoveOrganizationMembership } from "~/lib/iam/actions/mondoo/captain/Captain/RemoveOrganizationMembership";
import { ScopeType, useScope } from "~/hooks/useScope";
import { EditMemberRoleDialog } from "./EditMemberRoleDialog";
import { ConfirmationDialog } from "~/components/ConfirmationDialog/ConfirmationDialog";
import { ClickableTableRow } from "~/components/report";
import { DataTable } from "~/components/data-table";
import { AddButton } from "~/components/add-button";
import { getError } from "~/lib/handle-error";
import MembersEmptyState from "~/components/Members/MembersEmptyState";
import { SectionHeading } from "~/components/DetailsPage/components";
import { Member } from "~/components/Members/utils/membersUtils";
import { RoleCell } from "~/components/Members/RoleCell";
import {
  DEFAULT_PAGE_SIZE,
  INITIAL_PAGE_RANGE,
  PAGE_SIZE_PARAM,
  Pagination,
  PaginationRange,
} from "../pagination";
import { FilterBar } from "../FilterBar/FilterBar";
import { useSearch } from "../search/useSearch";

export type ResourceMembersProps = {
  handleAddMemberClick?: () => void;
};

type ResourceMembersState = {
  openEditMemberDialog: boolean;
  openRemoveMemberConfirmDialog: boolean;
  isRemovingMember: boolean;
  selectedItem?: Member;
};

export function ResourceMembers(props: ResourceMembersProps) {
  const { handleAddMemberClick } = props;
  const { viewer } = useViewer();
  const { activeScope, organizationScope, spaceScope } = useScope();
  const [searchParams] = useSearchParams();
  const [isEditMemberLoading, setIsEditMemberLoading] =
    useState<boolean>(false);
  const [pageItems, setPageItems] =
    useState<PaginationRange>(INITIAL_PAGE_RANGE);

  const pageSize =
    Number(searchParams.get(PAGE_SIZE_PARAM)) || DEFAULT_PAGE_SIZE;
  const isOrg = activeScope?.type === ScopeType.Organization;

  const spaceMrn = spaceScope?.mrn || "";
  const orgMrn = isOrg ? organizationScope?.mrn || "" : "";

  const [state, setState] = useState<ResourceMembersState>({
    openEditMemberDialog: false,
    openRemoveMemberConfirmDialog: false,
    isRemovingMember: false,
  });

  const mergeState = (params: Partial<ResourceMembersState>) => {
    return setState((prevState) => ({
      ...prevState,
      ...params,
    }));
  };

  const { enqueueSnackbar } = useSnackbar();
  const search = useSearch({ prefix: "members", multiple: false });
  const query = search.searchFilters[0] || "";

  const canSetSpaceMembership = useCanSetSpaceMembership();
  const canSetOrgMembership = useCanSetOrganizationMembership();
  const hasSetMembershipPermission =
    canSetSpaceMembership || canSetOrgMembership;

  const canRemoveSpaceMembership = useCanRemoveSpaceMembership();
  const canRemoveOrgMembership = useCanRemoveOrganizationMembership();
  const isCurrentUser = viewer?.mrn === state.selectedItem?.identity?.mrn;
  const hasRemoveMembershipPermission =
    !isCurrentUser && (canRemoveSpaceMembership || canRemoveOrgMembership);

  const [removeOrganizationMembership] =
    useRemoveOrganizationMembershipMutation();
  const [removeSpaceMembership] = useRemoveSpaceMembershipMutation();

  const removeMember = (userMrn: string) => {
    if (orgMrn) {
      return removeOrganizationMembership({
        variables: { input: { orgMrn, userMrn } },
        refetchQueries: [LoadOrganizationMembersDocument],
      });
    }
    if (spaceMrn) {
      return removeSpaceMembership({
        variables: { input: { spaceMrn, userMrn } },
        refetchQueries: [LoadSpaceMembersDocument],
      });
    }
  };

  const [setOrganizationMembership] = useSetOrganizationMembershipMutation();
  const [setSpaceMembership] = useSetSpaceMembershipMutation();

  const spaceMembersResult = useLoadSpaceMembersQuery({
    variables: {
      mrn: spaceMrn,
      first: pageSize,
      query: query || undefined,
    },
    skip: !spaceMrn,
  });
  const orgMembersResult = useLoadOrganizationMembersQuery({
    variables: {
      mrn: orgMrn,
      first: pageSize,
      query: query || undefined,
    },
    skip: !orgMrn,
  });

  const loading = spaceMembersResult.loading || orgMembersResult.loading;
  const error = spaceMembersResult.error || orgMembersResult.error;
  const spaceMembers =
    spaceMembersResult.data?.space?.members ??
    spaceMembersResult.previousData?.space?.members;
  const orgMembers =
    orgMembersResult.data?.organization?.members ??
    orgMembersResult.previousData?.organization?.members;
  const membersResult = spaceMrn ? spaceMembersResult : orgMembersResult;
  const members = spaceMembers || orgMembers;
  const pageInfo = members?.pageInfo;
  const totalCount = members?.totalCount || 0;
  const fetchMore = membersResult.fetchMore;

  if (loading && !members) {
    return <LoadingPage what="members" />;
  }

  if (error || !members) {
    return <LoadingFailedPage what="members" />;
  }

  const allMembers =
    members.edges?.flatMap((e) => (e.node ? [e.node] : [])) || [];

  const hasMembers = allMembers.length > 0;

  const handleOpenEditMember = (member: Member) => {
    mergeState({
      openEditMemberDialog: true,
      selectedItem: member,
    });
  };

  const handleRemoveConfirmMember = (member: Member) => {
    mergeState({
      openRemoveMemberConfirmDialog: true,
      selectedItem: member,
    });
  };

  const handleMemberRowClick = (member: Member) => {
    if (hasSetMembershipPermission || hasRemoveMembershipPermission) {
      handleOpenEditMember(member);
    }
  };

  const handleRemoveMemberCancel = () => {
    closeRemoveMemberConfirmDialog();
  };

  const handleRemoveMemberConfirm = async () => {
    try {
      mergeState({ isRemovingMember: true });
      const userMrn = state.selectedItem?.identity?.mrn;
      if (!userMrn) throw new Error("Member user mrn is required");

      await removeMember(userMrn);
      enqueueSnackbar(
        `User ${state?.selectedItem?.identity?.name} removed successfully.`,
        {
          variant: "success",
        },
      );
    } catch (e) {
      const errorMessage = getError(e);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      mergeState({ isRemovingMember: false });
      closeRemoveMemberConfirmDialog();
    }
  };

  const closeRemoveMemberConfirmDialog = () => {
    mergeState({
      openRemoveMemberConfirmDialog: false,
      selectedItem: undefined,
    });
  };

  const closeEditMemberDialog = () => {
    mergeState({
      openEditMemberDialog: false,
      selectedItem: undefined,
    });
  };

  const handleEditMemberCancel = () => {
    closeEditMemberDialog();
  };

  const handleEditMemberSave = async (roles: any) => {
    setIsEditMemberLoading(true);
    try {
      if (!orgMrn && !spaceMrn) throw new Error("Org or space mrn is required");
      const userMrn = state.selectedItem?.identity?.mrn;
      if (!userMrn) throw new Error("Member user mrn is required");

      if (orgMrn) {
        await setOrganizationMembership({
          variables: { input: { orgMrn, userMrn, roles } },
          refetchQueries: [LoadOrganizationMembersDocument],
        });
      }

      if (spaceMrn) {
        await setSpaceMembership({
          variables: { input: { spaceMrn, userMrn, roles } },
          refetchQueries: [LoadSpaceMembersDocument],
        });
      }

      enqueueSnackbar(
        `${state.selectedItem?.identity?.name} role edited successfully.`,
        {
          variant: "success",
        },
      );
    } catch (error) {
      const errorMessage = getError(error);
      if (errorMessage.includes("permission")) {
        enqueueSnackbar("You do not have permission to edit this member.", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Failed to edit member", {
          variant: "error",
        });
      }
    } finally {
      setIsEditMemberLoading(false);
      closeEditMemberDialog();
    }
  };

  return (
    <Grid size={12}>
      {state.selectedItem && (
        <EditMemberRoleDialog
          isLoading={isEditMemberLoading}
          open={state.openEditMemberDialog}
          member={state.selectedItem}
          onCancel={handleEditMemberCancel}
          onSave={handleEditMemberSave}
          onDelete={
            hasRemoveMembershipPermission
              ? () => handleRemoveConfirmMember(state.selectedItem!)
              : undefined
          }
        />
      )}
      {state.selectedItem && (
        <ConfirmationDialog
          isOpen={state.openRemoveMemberConfirmDialog}
          onClose={handleRemoveMemberCancel}
          onConfirm={handleRemoveMemberConfirm}
          loading={state.isRemovingMember}
          confirmButtonText="Remove Member"
          title="Remove Member"
          content={
            <Grid container>
              <Grid size={12}>
                <Typography>
                  Are you sure you want to remove{" "}
                  {state.selectedItem.identity?.name}?
                </Typography>
              </Grid>
            </Grid>
          }
        />
      )}
      <SectionHeading
        heading="Members"
        sx={{ marginTop: 0, marginBottom: 2 }}
      />
      {(hasMembers || (!hasMembers && Boolean(query))) && (
        <Box sx={{ display: "flex", mb: 3, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <FilterBar
              searchId="members-search"
              placeholder="members_search"
              search={search}
              mb={0}
            />
          </Box>
          {handleAddMemberClick && (
            <Box sx={{ pt: 1 }}>
              <AddButton
                id="invite-member-button"
                onClick={handleAddMemberClick}
                aria-label="Invite Member"
              />
            </Box>
          )}
        </Box>
      )}
      {!hasMembers && (
        <MembersEmptyState
          isSearched={Boolean(query)}
          handleAddMemberClick={handleAddMemberClick}
          isOrg={isOrg}
        />
      )}
      {hasMembers && (
        <>
          <DataTable id="member-list">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
              </TableRow>
              <TableRow sx={{ position: "relative", width: 1 }}>
                {loading && (
                  <LinearProgress
                    sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
                  />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {allMembers.slice(pageItems.from, pageItems.to).map((member) => {
                const { identity, roles = [] } = member;
                return (
                  <ClickableTableRow
                    key={identity?.mrn}
                    className="member-list-item"
                    hover
                    onClick={() => handleMemberRowClick(member)}
                    sx={{
                      cursor:
                        hasSetMembershipPermission ||
                        hasRemoveMembershipPermission
                          ? "pointer"
                          : "default",
                    }}
                  >
                    <TableCell>
                      {identity?.name}
                      {viewer?.mrn === identity?.mrn && " (You)"}
                    </TableCell>
                    <TableCell>
                      {identity.__typename === "User" ? identity.email : ""}
                    </TableCell>
                    <RoleCell roles={roles} />
                  </ClickableTableRow>
                );
              })}
            </TableBody>
          </DataTable>
          <Pagination
            totalCount={totalCount}
            fetchMore={fetchMore}
            pageInfo={pageInfo}
            setPageItems={setPageItems}
          />
        </>
      )}
    </Grid>
  );
}
